import { MobileController } from './mobile-controller.ts';

interface CharacterControls {
  switchRunToggle(): void;
}
const W = 'w';
const A = 'a';
const S = 's';
const D = 'd';
const DIRECTIONS = [W, A, S, D] as const;

type MovementDirection = {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  joystick?: { x: number; y: number };
};

export class InputController {
  keysPressed: Record<string, boolean> = {};
  private characterControls: CharacterControls | null = null;
  private mobileController: MobileController;
  private container: HTMLElement;
  private isMouseHidden = false;
  private isOrbiting = false; // 环绕状态标记
  private mouseHideTimer: number | null = null; // 鼠标隐藏定时器
  private mouseHideDelay = 3000; // 3秒后隐藏鼠标
  private isMousePressed = false; // 鼠标是否正在被按下
  private orbitButton: HTMLElement = null as unknown as HTMLElement; // 环绕按钮
  private onOrbitStart: (() => void) | null = null; // 环绕开始回调
  private onOrbitEnd: (() => void) | null = null; // 环绕结束回调

  constructor(container: HTMLElement) {
    this.container = container;
    this.mobileController = new MobileController(container);
    this.initEventListeners();
    this.createOrbitButton();
  }

  initEventListeners() {
    document.addEventListener('keydown', this.onKeyDown.bind(this), false);
    document.addEventListener('keyup', this.onKeyUp.bind(this), false);

    // PC端鼠标控制
    if (!this.mobileController.isMobileDevice()) {
      this.container.addEventListener('mousedown', this.onMouseDown.bind(this), false);
      this.container.addEventListener('mousemove', this.onMouseMove.bind(this), false);
      this.container.addEventListener('mouseup', this.onMouseUp.bind(this), false);
      document.addEventListener('keydown', this.onEscapeKey.bind(this), false);

      // 初始化时启动定时器
      this.resetMouseHideTimer();
    }
  }
  onMouseDown(): void {
    // PC端鼠标按下时隐藏鼠标并设置按下状态
    if (!this.mobileController.isMobileDevice()) {
      this.isMousePressed = true;
      this.hideMouse();
    }
  }

  onMouseMove(): void {
    // PC端鼠标移动时的处理
    if (!this.mobileController.isMobileDevice()) {
      // 只有在鼠标没有被按下时才显示鼠标
      if (!this.isMousePressed) {
        // 如果鼠标已隐藏，则显示鼠标
        if (this.isMouseHidden) {
          this.showMouse();
        }
        // 重置定时器
        this.resetMouseHideTimer();
      }
    }
  }

  onMouseUp(): void {
    // PC端鼠标释放时显示鼠标并启动定时器
    if (!this.mobileController.isMobileDevice()) {
      this.isMousePressed = false;
      this.showMouse();
      this.resetMouseHideTimer();
    }
  }

  onEscapeKey(event: KeyboardEvent): void {
    // 按ESC键显示鼠标
    if (event.key === 'Escape' && this.isMouseHidden) {
      this.showMouse();
    }
  }

  hideMouse(): void {
    this.container.style.cursor = 'none';
    this.isMouseHidden = true;
    // 清除定时器
    this.clearMouseHideTimer();
  }

  showMouse(): void {
    this.container.style.cursor = 'default';
    this.isMouseHidden = false;
    // 清除定时器
    this.clearMouseHideTimer();
  }
  // 重置鼠标隐藏定时器
  resetMouseHideTimer(): void {
    // 清除现有定时器
    this.clearMouseHideTimer();
    // 设置新的定时器
    this.mouseHideTimer = setTimeout(() => {
      if (!this.isMouseHidden) {
        this.hideMouse();
      }
    }, this.mouseHideDelay);
  }
  // 清除鼠标隐藏定时器
  clearMouseHideTimer(): void {
    if (this.mouseHideTimer) {
      clearTimeout(this.mouseHideTimer);
      this.mouseHideTimer = null;
    }
  }
  onKeyDown(event: KeyboardEvent): void {
    if (event.shiftKey && this.characterControls) {
      this.characterControls.switchRunToggle();
    } else {
      this.keysPressed[event.key.toLowerCase()] = true;
    }
  }

  onKeyUp(event: KeyboardEvent): void {
    this.keysPressed[event.key.toLowerCase()] = false;
  }

  setCharacterControls(characterControls: any): void {
    this.characterControls = characterControls;
  }

  isDirectionPressed(): boolean {
    // 环绕时禁用所有移动输入
    if (this.isOrbiting) return false;
    // 检查键盘输入或移动端摇杆输入
    const keyboardPressed = DIRECTIONS.some((key) => this.keysPressed[key] === true);
    const mobileInput = this.mobileController.getMovementInput();
    const mobilePressed = Math.abs(mobileInput.x) > 0.1 || Math.abs(mobileInput.y) > 0.1;
    return keyboardPressed || mobilePressed;
  }

  isKeyPressed(key: string): boolean {
    return this.keysPressed[key] === true;
  }

  getMovementDirection(): MovementDirection {
    // 环绕时禁用所有移动输入
    if (this.isOrbiting) {
      return {
        forward: false,
        backward: false,
        left: false,
        right: false,
        joystick: { x: 0, y: 0 },
      };
    }
    // 优先使用移动端输入
    if (this.mobileController.isMobileDevice()) {
      const mobileInput = this.mobileController.getMovementInput();
      if (Math.abs(mobileInput.x) > 0.1 || Math.abs(mobileInput.y) > 0.1) {
        return {
          forward: mobileInput.y > 0.1,
          backward: mobileInput.y < -0.1,
          left: mobileInput.x < -0.1,
          right: mobileInput.x > 0.1,
          joystick: mobileInput,
        };
      }
    }
    // 键盘输入
    return {
      forward: this.keysPressed[W],
      backward: this.keysPressed[S],
      left: this.keysPressed[A],
      right: this.keysPressed[D],
    };
  }

  getDirectionOffset(): number {
    // 环绕时禁用所有移动输入
    if (this.isOrbiting) return 0;
    // 优先处理移动端摇杆输入
    if (this.mobileController.isMobileDevice()) {
      const mobileInput = this.mobileController.getMovementInput();
      if (Math.abs(mobileInput.x) > 0.1 || Math.abs(mobileInput.y) > 0.1) {
        return Math.atan2(-mobileInput.x, mobileInput.y);
      }
    }
    // 键盘输入的方向偏移计算
    let directionOffset = 0; // w

    if (this.keysPressed[W]) {
      if (this.keysPressed[A]) {
        directionOffset = Math.PI / 4; // w+a
      } else if (this.keysPressed[D]) {
        directionOffset = -Math.PI / 4; // w+d
      }
    } else if (this.keysPressed[S]) {
      if (this.keysPressed[A]) {
        directionOffset = Math.PI / 4 + Math.PI / 2; // s+a
      } else if (this.keysPressed[D]) {
        directionOffset = -Math.PI / 4 - Math.PI / 2; // s+d
      } else {
        directionOffset = Math.PI; // s
      }
    } else if (this.keysPressed[A]) {
      directionOffset = Math.PI / 2; // a
    } else if (this.keysPressed[D]) {
      directionOffset = -Math.PI / 2; // d
    }

    return directionOffset;
  }
  // 获取跑步状态（移动端按钮或键盘Shift）
  getRunToggleState(): boolean {
    if (this.mobileController.isMobileDevice()) {
      return this.mobileController.getRunState();
    }
    return false; // 键盘通过Shift临时切换，这里返回false
  }
  // 获取环绕状态
  getOrbitState() {
    // return this.mobileController.getOrbitState();
  }
  // 设置环绕回调函数
  // 创建环绕按钮
  createOrbitButton() {
    const orbitButton = document.createElement('div');
    orbitButton.setAttribute('id', 'orbitButton');
    orbitButton.innerHTML = '环顾';
    orbitButton.style.cssText = `
            position: fixed;
            bottom: 50px;
            right: 50px;
            width: 80px;
            height: 80px;
            background: rgba(255, 255, 255, 0.3);
            border: 2px solid rgba(255, 255, 255, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: bold;
            color: white;
            z-index: 1000;
            touch-action: none;
            user-select: none;
        `;

    this.container.parentNode!.appendChild(orbitButton);
    this.orbitButton = orbitButton;

    this.addOrbitButtonEvents();
  }
  // 添加环绕按钮事件
  addOrbitButtonEvents() {
    const handleStart = (e) => {
      e.preventDefault();
      this.isOrbiting = true;
      this.orbitButton.style.background = 'rgba(100, 255, 100, 0.6)';

      // 触发环绕开始事件
      if (this.onOrbitStart) {
        this.onOrbitStart();
      }
    };

    const handleEnd = (e) => {
      e.preventDefault();
      this.isOrbiting = false;
      this.orbitButton.style.background = 'rgba(255, 255, 255, 0.3)';

      // 触发环绕结束事件
      if (this.onOrbitEnd) {
        this.onOrbitEnd();
      }
    };

    // 移动端触摸事件
    this.orbitButton.addEventListener('touchstart', handleStart, {
      passive: false,
    });
    this.orbitButton.addEventListener('touchend', handleEnd, {
      passive: false,
    });
    this.orbitButton.addEventListener('touchcancel', handleEnd, {
      passive: false,
    });

    // PC端鼠标事件
    this.orbitButton.addEventListener('mousedown', handleStart, {
      passive: false,
    });
    this.orbitButton.addEventListener('mouseup', handleEnd, {
      passive: false,
    });
    this.orbitButton.addEventListener('mouseleave', handleEnd, {
      passive: false,
    });
  }

  // 设置环绕回调函数
  setOrbitCallbacks(onStart: () => void, onEnd: () => void): void {
    this.onOrbitStart = onStart;
    this.onOrbitEnd = onEnd;
  }

  destroy(): void {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);

    // 清理PC端事件监听器
    if (!this.mobileController.isMobileDevice()) {
      this.container.removeEventListener('mousedown', this.onMouseDown);
      this.container.removeEventListener('mousemove', this.onMouseMove);
      this.container.removeEventListener('mouseup', this.onMouseUp);
      document.removeEventListener('keydown', this.onEscapeKey);
      // 清理定时器
      this.clearMouseHideTimer();
      // 恢复鼠标显示
      this.showMouse();
    }

    // 清理环绕按钮
    if (this.orbitButton) {
      this.orbitButton.remove();
    }

    this.mobileController.destroy();
  }
}
